import _                                                          from 'underscore';
import React, { FC, useContext, useState }                        from 'react';
import { FieldArray, Formik, FormikConfig }                       from 'formik';
import * as Yup                                                   from 'yup';
import { Button, Checkbox, Form, Icon, List, Menu, Segment, Tab } from 'semantic-ui-react';
import { useMutation, useQuery }                                  from '@apollo/react-hooks';

import { CorvalAccount, FlexAlign, Size, Uuid }                     from '@src/types';
import { UserQueries }                                              from '@src/queries';
import { useCurrentUser, useCustomFormikHelpers, useYupExtensions } from '@src/hooks';
import { queriesHaveData, queriesLoading, withoutType }             from '@src/store/_apolloUtil';
import { Flexbox, FlexItem, StyledContainer }                       from '@src/views/containers';
import { LoadingIndicator }                                         from '@src/views/components/util/loading/LoadingIndicator';
import { HelpAndSupportStatement }                                  from '@src/views/components/util/messaging/HelpAndSupportStatement';
import { FormTabWithNotifier }                                      from '@src/views/components/display/ItemWithNotifier';
import { FieldDescription, FieldWrapper, TextField }                from '@src/views/components/forms';
import { FieldLabel }                                               from '@src/views/components/forms/formik/FieldLabel';
import { FinePrintMessage }                                         from '@src/views/components/util/messaging/FinePrintMessage';
import { Tooltip }                                                  from '@src/views/components/util/messaging/ToolTip';
import { DynamicComponentsContext }                                 from '@src/views/contexts';
import { useSystemData }                                            from '@src/hooks/useSystemData';
import { Cell, Grid }                                               from 'styled-css-grid';
import { getCSSVar }                                                from '@src/util';


interface UserProfileEditorProps {
    accountId: Uuid;
}

const defaultProps: UserProfileEditorProps = {
    accountId: null
};

interface UserProfileEditorValues {
    id: Uuid,
    firstName: string,
    lastName: string,
    emailAddress: string,
    businessFunctionIds: Uuid[]
}

type UserProfileEditorConfig = FormikConfig<UserProfileEditorValues>

interface IAccountDetailsReq {
    accounts: CorvalAccount[];
}

const userProfileEditorDefaultValues: UserProfileEditorValues = {
    id:                  '',
    firstName:           '',
    lastName:            '',
    emailAddress:        '',
    businessFunctionIds: [],
};

const UserDetailsEditor: FC<UserProfileEditorProps> = ( props ) => {

    let { accountId }                       = props,
        { currentUser }                     = useCurrentUser(),
        systemData                          = useSystemData(),
        { toasts: [ , toastActions ] }      = useContext( DynamicComponentsContext ),
        [ isOperating, setIsOperating ]     = useState( false ),
        { addStringIsValidPhoneNumberTest } = useYupExtensions(),
        { includesErrorsIn }                = useCustomFormikHelpers();

    // Call to get account
    let getAccountDetailsReq = useQuery<IAccountDetailsReq>(
        UserQueries.queries.GET_USER( UserQueries.fragments.USER.EDIT_FIELDS ), {
            variables:   { id: accountId ?? currentUser.id },
            fetchPolicy: 'cache-and-network'
        }
    );

    // Call to update account
    let [ updateUser, updateUserReq ] = useMutation( UserQueries.mutations.UPDATE_USER_ACCOUNT() );

    // Flags
    let isLoading = queriesLoading( [ getAccountDetailsReq, updateUserReq ] );

    const handleSubmission: UserProfileEditorConfig['onSubmit'] = async ( values ) => {

        try{

            let data = {

                /* Remove unsupported items */
                ..._.omit( values, 'emailAddress' )
            };

            await updateUser( {
                variables:           {
                    input: data
                },
                refetchQueries:      [
                    { query: UserQueries.queries.GET_USER(), variables: { id: accountId ?? currentUser.id } },
                    { query: UserQueries.queries.GET_CURRENT_USER() }
                ],
                awaitRefetchQueries: true

            } ).then( ( response ) => {

                if( response ){

                    toastActions.success( `Profile updated successfully!` );

                } else {

                    toastActions.error( 'Mutation Error', updateUserReq.error.message, { timeoutMS: 0 } );
                }

            } );

        } catch( e ) {

            toastActions.error( 'Network Error', `${ e.message }` );
        }

        setIsOperating( false );
    };

    const getMappedItemReqData = (): UserProfileEditorValues => {

        /* Do we have remote date of user or not ? */
        if( !queriesHaveData( getAccountDetailsReq ) ) return userProfileEditorDefaultValues;

        /* Translate objects into form data attributes that
         match the mutation requests for create / update */
        let reqData = _.first( getAccountDetailsReq.data.accounts );

        // Curate user's business functions to only those that are not archived.
        const availableBusinessFunctionsOnly = _.filter( reqData.businessFunctions, businessFunction => !_.findWhere(systemData.businessFunctions, { id: businessFunction.id })?.isArchived )

        return {
            ...userProfileEditorDefaultValues,
            ..._.omit( withoutType( reqData ), 'projects', 'updatedUtc', 'businessFunctions' ),
            businessFunctionIds: availableBusinessFunctionsOnly
        };
    };

    /* FORM SETUP */
    /* ---------------------------------------------------------*/
    addStringIsValidPhoneNumberTest();

    let formProps: UserProfileEditorConfig = {
        enableReinitialize: true,
        initialValues:      getMappedItemReqData(),
        validationSchema:   Yup.object().shape( {
            firstName:    Yup.string().required( 'A first name is required' ),
            lastName:     Yup.string().required( 'A last name is required' ),
            emailAddress: Yup.string().email().required( 'An email address is required' )
        } ),
        onSubmit:           handleSubmission
    };

    return (

        <div>
            <Formik { ...formProps }>

                { formik => {

                    let canSave = formik.dirty && formik.isValid && !( isLoading || isOperating );

                    let hasGeneralFieldErrors = includesErrorsIn( formik, 'firstName', 'lastName' ),
                        hasTeamFieldErrors    = includesErrorsIn( formik, 'businessFunctionIds' ),
                        hasSettingFieldErrors = includesErrorsIn( formik );

                    return (
                        <Form onSubmit={ formik.handleSubmit } onReset={ formik.handleReset }>

                            {/* Controls */ }
                            <Flexbox>
                                <FlexItem>
                                    <Button basic
                                            type={ 'button' }
                                            icon={ 'undo' }
                                            onClick={ () => formik.resetForm() }
                                            disabled={ !formik.dirty || isOperating }
                                            content={ 'Reset' }/>

                                </FlexItem>
                                <FlexItem noGrow>
                                    <Button type={ 'button' }
                                            primary={ canSave }
                                            icon={ 'save' }
                                            content={ 'Update' }
                                            onClick={ () => formik.submitForm() }
                                            disabled={ !canSave }/>
                                </FlexItem>
                            </Flexbox>

                            {/* Form Fields */ }
                            <Segment color={ 'grey' } padded>

                                <LoadingIndicator label={ 'Loading...' } isActive={ isOperating } inverted/>

                                <Tab
                                    menu={ {
                                        secondary: true,
                                        pointing:  true,
                                        color:     'purple'
                                    } }
                                    defaultActiveIndex={ 0 }
                                    onTabChange={ ( e, { activeIndex } ) => {} }
                                    renderActiveOnly={ true }
                                    panes={ [
                                        {

                                            menuItem: <Menu.Item key={ 'general' }><FormTabWithNotifier label={ 'General' } active={ hasGeneralFieldErrors }/></Menu.Item>,
                                            render:   () => (
                                                <Tab.Pane className={ 'no-border' }>

                                                    <Flexbox cols={ 2 } gutterSize={ Size.LG }>
                                                        <FlexItem>

                                                            <StyledContainer padding={ [ null, null, Size.MD, null ] }>

                                                                <FieldWrapper includeErrorMessage={ false } showErrors={ false }>

                                                                    <FieldLabel for={ 'firstName' }><Icon name={ 'user' } color={ 'purple' }/> Display Name</FieldLabel>
                                                                    <FieldDescription>Your first and last name as displayed to team members</FieldDescription>
                                                                    <Flexbox cols={ 2 } gutterSize={ Size.XS } align={ FlexAlign.END }>
                                                                        <FlexItem>

                                                                            <TextField
                                                                                type="text"
                                                                                name={ 'firstName' }
                                                                                placeholder={ 'First Name' }
                                                                                includeErrorMessage={ !_.isEmpty( formik.values.firstName ) }
                                                                                loading={ isLoading }/>

                                                                        </FlexItem>
                                                                        <FlexItem>

                                                                            <TextField
                                                                                type="text"
                                                                                name={ 'lastName' }
                                                                                placeholder={ 'Last Name' }
                                                                                includeErrorMessage={ !_.isEmpty( formik.values.lastName ) }
                                                                                loading={ isLoading }/>

                                                                        </FlexItem>
                                                                    </Flexbox>
                                                                </FieldWrapper>
                                                            </StyledContainer>

                                                            <StyledContainer margin={ [ Size.MD, null ] }>

                                                                <TextField
                                                                    type="email"
                                                                    label={ <><Icon name={ 'envelope' } color={ 'purple' }/> Email Address</> }
                                                                    descriptionText={
                                                                        <>
                                                                            Email is used to log in and send correspondence
                                                                            { ' ' }<Tooltip popupProps={ { closeOnTriggerMouseLeave: false } } content={ <>Account email address cannot be updated. <HelpAndSupportStatement/></> }/>
                                                                        </>
                                                                    }
                                                                    name={ 'emailAddress' }
                                                                    input={ {
                                                                        className: 'border--colored-lt-gray'
                                                                    } }
                                                                    readOnly={ true }
                                                                    loading={ isLoading }/>
                                                                <FinePrintMessage>This field is readonly.</FinePrintMessage>

                                                            </StyledContainer>

                                                        </FlexItem>

                                                        <FlexItem>

                                                            <StyledContainer padding={ [ null, null, Size.MD, null ] }>


                                                                {/*<List relaxed={ 'very' }>

                                                                 <List.Item>

                                                                 <FieldLabel><Icon name={ 'cog' } color={ 'purple' }/> Account Tools</FieldLabel>
                                                                 <FieldDescription>You can service your account with the following options.</FieldDescription>

                                                                 <List divided className={ 'no-padding' }>

                                                                 <List.Item>


                                                                 </List.Item>

                                                                 </List>

                                                                 </List.Item>

                                                                 </List>*/ }

                                                            </StyledContainer>

                                                        </FlexItem>
                                                    </Flexbox>

                                                </Tab.Pane>

                                            )
                                        }, {

                                            menuItem: <Menu.Item key={ 'teams' }><FormTabWithNotifier label={ 'Roles' } active={ hasTeamFieldErrors }/></Menu.Item>,
                                            render:   () => (
                                                <Tab.Pane className={ 'no-border' }>

                                                    <StyledContainer padding={ [ null, null, Size.MD, null ] }>

                                                        <FieldWrapper includeErrorMessage={ false } showErrors={ false }>

                                                            <FieldLabel><Icon name={ 'users' } color={ 'purple' }/> Team Functions</FieldLabel>
                                                            <FieldDescription>Team functions help identify your areas of expertise so that Corval knows what types of items to involve you in. You should choose at least one area that applies to your business knowledge</FieldDescription>
                                                            <StyledContainer padding={ [ Size.MD, null, null, null ] }>


                                                                <FieldArray
                                                                    name="businessFunctionIds"
                                                                    render={ arrayHelpers => (

                                                                        <List size={ 'small' } relaxed divided>
                                                                            { _.filter( systemData.businessFunctions, bf => !bf.isArchived ).map( ( businessFunction, index ) => {

                                                                                return (
                                                                                    <List.Item key={ businessFunction.id }>

                                                                                        <Grid columns={ `5px 1fr` }>

                                                                                            <Cell middle style={ { height: '40px', backgroundColor: getCSSVar( `--color-${ businessFunction.colorValue }` ) } }></Cell>
                                                                                            <Cell middle>
                                                                                                <Checkbox id={ `bf.${ businessFunction.id }` }
                                                                                                          label={ businessFunction.name }
                                                                                                          checked={ formik.values.businessFunctionIds.includes( businessFunction.id ) }
                                                                                                          onChange={ ( e, data ) => {
                                                                                                              ( data.checked )
                                                                                                              ? arrayHelpers.push( businessFunction.id )
                                                                                                              : formik.setFieldValue( 'businessFunctionIds', _.without( formik.values.businessFunctionIds, businessFunction.id ) );
                                                                                                          } }
                                                                                                />
                                                                                            </Cell>
                                                                                        </Grid>

                                                                                    </List.Item>
                                                                                );
                                                                            } ) }
                                                                        </List>

                                                                    ) }
                                                                />
                                                            </StyledContainer>

                                                        </FieldWrapper>

                                                    </StyledContainer>

                                                </Tab.Pane>
                                            )
                                        }, /*{

                                         menuItem: <Menu.Item key={ 'settings' }><FormTabWithNotifier label={ 'Settings' } active={ hasSettingFieldErrors }/></Menu.Item>,
                                         render:   () => (
                                         <Tab.Pane className={ 'no-border' }>



                                         </Tab.Pane>
                                         )
                                         }*/

                                    ] }>

                                </Tab>

                            </Segment>

                        </Form>
                    );
                } }

            </Formik>
        </div>
    );
};

UserDetailsEditor.defaultProps = defaultProps;

export { UserDetailsEditor };
